import React from "react";
import { LegalPage } from "@/pages/LegalPage";

const SECTIONS = [
  {
    h: "Who you're dealing with",
    p: [
      "This site is operated by Dan Berry Music, based in Broward County, Florida, USA. It covers all of Dan's work in one place: performances and bookings, private and community events, soundbaths, commissions, the podcast, patronage, and Alephonics — the Alephonic Mapping Method™, its podcast and its Try It demo — which previously lived on its own site.",
      "By using the site you're agreeing to what follows. If you don't agree with it, please don't use the site.",
    ],
  },
  {
    h: "The method and the music belong to Dan",
    p: [
      "The Alephonic Mapping Method™ — the system, its rules, its name, and the software that applies it — is Dan Berry's work and remains his property. The recordings, compositions, writing, artwork, photography and design on this site are his too, or used with permission.",
      "You're welcome to listen, share links, quote us with credit, and play what the demo gives you for yourself, at home, in your own study, with your family. That's what it's for.",
      "What you may not do without written permission: republish or resell the audio, use it in a commercial or public performance, use it in a video, film, advertisement or app, feed the site's output into a machine-learning system or training set, or reproduce the mapping rules as your own system or product.",
    ],
  },
  {
    h: "The demo, your account and the studio",
    p: [
      "The Try It page applies the method to a word or verse you choose and plays the result. The output is generated for you to hear; the underlying method stays ours. Personal, non-commercial listening is yours without asking.",
      "If you create an account, keep your sign-in to yourself and tell us if you think it's been used by somebody else. Compositions you save are yours to keep and ours to store; we may remove an account or its contents if it's used to break these terms, and you can ask us to delete either at any time.",
      "The Hebrew text on the site comes from Sefaria and carries its own licensing, which belongs to Sefaria and the rights holders of each text, not to us.",
    ],
  },
  {
    h: "Bookings and live events",
    p: [
      "Concerts, soundbaths, lifecycle events, community events and Shabbaton weekends are confirmed in writing — date, venue, fee, travel and what's included — before anything is held. Prices quoted on the site are starting points, not offers; the written agreement is what governs the event.",
      "Deposits hold a date and are non-refundable once a date is held, because holding it means turning other work away. Cancellation, postponement and travel terms live in that agreement. If something outside anyone's control makes an event impossible, we'll work with you to move it rather than simply keep the money.",
      "At an interactive show the audience sends in words, names and requests from their phones, and they may appear on the screen. Please keep submissions decent and legal; anything else may be removed, and we may close submissions. By taking part you accept that the room may be photographed, filmed or recorded, and that such material may be used to promote Dan's work.",
    ],
  },
  {
    h: "Commissions",
    p: [
      "A commission is agreed in writing before any work starts — scope, timeline, fee. Unless that written agreement says otherwise, Dan retains copyright in the composition and the recording, and you receive a personal-use licence: listen to it, play it in your home, share it with family and friends at a private occasion.",
      "Anything beyond that — commercial use, public performance, broadcast, use in a film or product, or resale — needs a separate written licence, which we're glad to discuss.",
      "Commission fees are for the work of making the piece. Once work has begun they are non-refundable, though if something goes wrong on our end we will always talk it through with you.",
    ],
  },
  {
    h: "Patronage — Support the Music",
    p: [
      "Patronage is a voluntary recurring monthly contribution at the tier you choose, billed automatically each month until you cancel. It keeps the weekly Alephonics podcast and the free demo going. Payments are handled by Stripe and/or Systeme.io.",
      "What you get in return: your name on the patrons wall while your patronage is active, a credit on any album released during that time, a podcast shoutout, and the behind-the-scenes group. It is support for the work, not the purchase of a product, and it isn't a charitable donation for tax purposes.",
      "You can cancel any time. Cancelling stops future billing, and your name comes off the wall at the end of the period you've already paid for. We don't refund partial months, and there's no minimum term keeping you in. Tiers and prices can change, but never mid-month and never without telling you first by email.",
      "Patron-only material is for patrons. Please don't redistribute it or post it publicly.",
    ],
  },
  {
    h: "Emails",
    p: [
      "If you give us your email address we'll use it to send you what you signed up for — updates, tour alerts near you, podcast news. Every email has a working unsubscribe link, and using it costs you nothing else: you keep access to everything on the site.",
    ],
  },
  {
    h: "Using the site decently",
    p: [
      "Don't try to break in, scrape the site at scale, hammer the demo with automated requests, resell access to it, or use it to harass anyone. Don't submit anything unlawful or hateful through the forms or at a live show. We may cut off access if any of that happens.",
    ],
  },
  {
    h: "What this is and isn't",
    p: [
      "This is music: performance, composition, and music made from Hebrew letters. It is not rabbinic instruction, halachic guidance, therapy, or medical or mental-health treatment, and nothing here — soundbaths included — should be taken as any of those.",
      "The site is provided as it is. We work hard to keep it accurate and available, but we can't guarantee it will never be down, never have a bug, or always suit your purpose. To the fullest extent the law allows, we're not liable for indirect or consequential losses, and any liability we do have is limited to what you've paid us in the twelve months before the claim. Nothing here limits the terms of a signed booking or commission agreement, which take precedence for that event or piece.",
    ],
  },
  {
    h: "Links and outside services",
    p: [
      "Some pages embed or link to other services — YouTube, Sefaria, our email provider and our payment processors. We don't control them and aren't responsible for their content or their terms.",
    ],
  },
  {
    h: "Governing law",
    p: [
      "These terms are governed by the laws of the State of Florida, USA, and any dispute belongs in the state or federal courts of Broward County, Florida. Before it ever gets there, please write to us — almost everything can be sorted out in an email.",
    ],
  },
  {
    h: "Changes",
    p: [
      "We may update these terms. The date at the top shows the current version, and continuing to use the site after a change means you accept it. This version reflects Alephonics and Dan Berry Music becoming one site, and replaces the separate Alephonics terms.",
    ],
  },
];

export default function Terms() {
  return (
    <LegalPage
      active="/terms"
      eyebrow="Terms"
      title="Terms & Conditions"
      intro="The rules of the road for the whole site: what's ours, what's yours, and how bookings, commissions and patronage work."
      sections={SECTIONS}
    />
  );
}
